import apiClient, { handleApiResponse, handleApiError } from './apiClient';

/**
 * User Management Service
 * Handles HR user management operations for managers
 */
class UserManagementService {
  /**
   * Get all HR users with pagination and filtering
   * @param {object} options - Query options
   * @returns {Promise<object>} Users list with pagination
   */
  async getHRUsers(options = {}) {
    try {
      const params = new URLSearchParams();
      
      if (options.page) params.append('page', options.page);
      if (options.pageSize) params.append('page_size', options.pageSize);
      if (options.role) params.append('role', options.role);
      if (options.search) params.append('search', options.search);
      
      const response = await apiClient.get(`/user-management/users?${params}`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Alias for getHRUsers - used by task management
   * @param {object} options - Query options
   * @returns {Promise<object>} Users list with pagination
   */
  async getUsers(options = {}) {
    return this.getHRUsers(options);
  }

  /**
   * Create new HR user (coordinators, recruiters, etc.)
   * @param {object} userData - User information
   * @returns {Promise<object>} Created user
   */
  async createHRUser(userData) {
    try {
      const response = await apiClient.post('/user-management/users', userData);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Update HR user information
   * @param {string} userId - User UUID
   * @param {object} userData - Updated user information
   * @returns {Promise<object>} Updated user
   */
  async updateHRUser(userId, userData) {
    try {
      const response = await apiClient.put(`/user-management/users/${userId}`, userData);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Delete (deactivate) HR user
   * @param {string} userId - User UUID
   * @returns {Promise<object>} Deletion confirmation
   */
  async deleteHRUser(userId) {
    try {
      const response = await apiClient.delete(`/user-management/users/${userId}`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Reset user password
   * @param {string} userId - User UUID
   * @returns {Promise<object>} New temporary password
   */
  async resetUserPassword(userId) {
    try {
      const response = await apiClient.post(`/user-management/users/${userId}/reset-password`);
      return handleApiResponse(response);
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Get user statistics for managers
   * @returns {Promise<object>} User statistics
   */
  async getUserStats() {
    try {
      // Get users and calculate stats
      const allUsers = await this.getHRUsers({ pageSize: 1000 });
      
      const stats = {
        total: allUsers.total || 0,
        active: allUsers.users?.filter(u => u.is_active).length || 0,
        byRole: {},
        recentlyAdded: allUsers.users?.filter(u => {
          const createdAt = new Date(u.created_at);
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          return createdAt > weekAgo;
        }).length || 0
      };

      // Count by role
      allUsers.users?.forEach(user => {
        stats.byRole[user.role] = (stats.byRole[user.role] || 0) + 1;
      });

      return stats;
    } catch (error) {
      handleApiError(error);
    }
  }

  /**
   * Check if current user can manage other users
   * @returns {Promise<boolean>} Whether user has manager permissions
   */
  async canManageUsers() {
    try {
      // Try to get users - if it fails, user doesn't have permission
      await this.getHRUsers({ pageSize: 1 });
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get available roles for HR users
   * @returns {Array} List of available roles
   */
  getAvailableRoles() {
    return [
      {
        value: 'hr_manager',
        label: 'HR Manager',
        description: 'Full HR operations access and team management'
      },
      {
        value: 'recruiter',
        label: 'Recruiter',
        description: 'Candidate and interview management'
      },
      {
        value: 'hr_coordinator',
        label: 'HR Coordinator',
        description: 'Administrative coordination and scheduling'
      },
      {
        value: 'hr_user',
        label: 'HR User',
        description: 'Basic HR user access'
      }
    ];
  }

  /**
   * Validate user data before submission
   * @param {object} userData - User data to validate
   * @returns {object} Validation result
   */
  validateUserData(userData) {
    const errors = {};

    if (!userData.email || !userData.email.includes('@')) {
      errors.email = 'Valid email is required';
    }

    if (!userData.name || userData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }

    if (!userData.role) {
      errors.role = 'Role is required';
    }

    if (userData.password && userData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  /**
   * Generate user display information
   * @param {object} user - User object
   * @returns {object} Display information
   */
  getUserDisplayInfo(user) {
    const roleInfo = this.getAvailableRoles().find(r => r.value === user.role);
    
    return {
      ...user,
      roleLabel: roleInfo?.label || user.role,
      roleDescription: roleInfo?.description || '',
      statusLabel: user.is_active ? 'Active' : 'Inactive',
      statusColor: user.is_active ? 'success' : 'warning',
      createdAtFormatted: new Date(user.created_at).toLocaleDateString(),
      avatar: user.avatar || this.generateAvatarUrl(user.name)
    };
  }

  /**
   * Generate avatar URL from name
   * @param {string} name - User name
   * @returns {string} Avatar URL
   */
  generateAvatarUrl(name) {
    const initials = name
      .split(' ')
      .map(n => n.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
    
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=3B82F6&color=fff&size=40`;
  }
}

export default new UserManagementService();